'use client';

import { c as _c } from "react/compiler-runtime";
import { useLocale, useTranslations } from 'next-intl';
import { AnimatedButton } from '@/components/ui/animated-button';
import { Icon, type IconSvgElement } from '@/components/ui/icon';
import { Award01IconSolid, Rocket01IconSolid } from '@/components/ui/icon/glyphs';
import { useMarketOutcomeColor } from '@/hooks/chart/use-outcome-color';
import { useLiveClock } from '@/hooks/live-events/use-live-clock';
import { Link } from '@/i18n/navigation';
import { formatLocalDateTime } from '@/lib/format/format-date';
import { cn } from '@/lib/ui/utils';
import type { LifecycleModalPayload, LifecycleVariant } from '@/stores/market-lifecycle-modal';
export interface LifecycleModalViewProps {
  payload: LifecycleModalPayload;
  /** Dismiss the modal before the selected CTA continues. */
  onClose: () => void;
}
type Tone = 'positive' | 'negative' | 'brand';
type PrimaryCta = 'trade' | 'claim' | 'browse';
interface VariantPresentation {
  tone: Tone;
  heroIcon: IconSvgElement;
  /** Show the winning-outcome label chip (resolution variants only). */
  showWinnerChip: boolean;
  primary: PrimaryCta;
  /** Show the secondary "Claim backer rewards" CTA. */
  showBackerCta: boolean;
}
const TONE_CLASSES: Record<Tone, {
  glow: string;
  iconWrap: string;
  icon: string;
}> = {
  positive: {
    glow: 'lifecycle-glow-positive',
    iconWrap: 'bg-feedback-success-surface',
    icon: 'text-feedback-success-content'
  },
  negative: {
    glow: 'lifecycle-glow-negative',
    iconWrap: 'bg-feedback-error-surface',
    icon: 'text-feedback-error-content'
  },
  brand: {
    glow: 'lifecycle-glow-brand',
    iconWrap: 'bg-background-brand',
    icon: 'text-foreground-brand'
  }
};
function toneForVariant(variant: LifecycleVariant): Tone {
  switch (variant) {
    case 'resolution-won-nonbacker':
    case 'resolution-won-backer':
      return 'positive';
    case 'resolution-lost-nonbacker':
    case 'resolution-lost-backer':
      return 'negative';
    default:
      return 'brand';
  }
}
function presentationFor(variant: LifecycleVariant, isBacker: boolean): VariantPresentation {
  const tone = toneForVariant(variant);
  switch (variant) {
    case 'graduation-generic':
    case 'graduation-backer':
    case 'graduation-creator':
      return {
        tone,
        heroIcon: Rocket01IconSolid,
        showWinnerChip: false,
        primary: 'trade',
        showBackerCta: false
      };
    case 'resolution-won-nonbacker':
      return {
        tone,
        heroIcon: Award01IconSolid,
        showWinnerChip: true,
        primary: 'claim',
        showBackerCta: false
      };
    case 'resolution-won-backer':
      return {
        tone,
        heroIcon: Award01IconSolid,
        showWinnerChip: true,
        primary: 'claim',
        showBackerCta: true
      };
    case 'resolution-lost-nonbacker':
      return {
        tone,
        heroIcon: Award01IconSolid,
        showWinnerChip: true,
        primary: 'browse',
        showBackerCta: false
      };
    case 'resolution-lost-backer':
      return {
        tone,
        heroIcon: Award01IconSolid,
        showWinnerChip: true,
        primary: 'browse',
        showBackerCta: true
      };
    case 'resolution-not-holder':
      // A pure backer with no outcome holdings still gets the backer claim path.
      return {
        tone,
        heroIcon: Award01IconSolid,
        showWinnerChip: true,
        primary: 'browse',
        showBackerCta: isBacker
      };
  }
}

/**
 * Presentational renderer for the lifecycle-modal variants. Branches on
 * `payload.variant` to pick a tone (corner glow), hero icon, copy, and the
 * primary / secondary CTAs. No animation is added here, so reduced-motion is
 * satisfied by construction (the only motion is SilkSheet's own overlay
 * transition, which already honors `prefers-reduced-motion`).
 *
 * NOTE (spec 112, FR-018): the hero is currently an icon-based placeholder.
 * When the final Figma art lands, swap the hero for the winning-outcome image
 * — render it via `OptimizedImage`/`OutcomeIndicator` so the NSFW overlay is
 * threaded (the `winningOutcome*` payload fields are carried for that purpose).
 */
export function LifecycleModalView(t0) {
  const $ = _c(102);
  const {
    payload,
    onClose
  } = t0;
  const t = useTranslations("market.lifecycle_modal");
  const scheduledBody = useScheduledGraduationCopy(payload);
  let t1;
  if ($[0] !== payload.isBacker || $[1] !== payload.variant) {
    t1 = presentationFor(payload.variant, payload.isBacker);
    $[0] = payload.isBacker;
    $[1] = payload.variant;
    $[2] = t1;
  } else {
    t1 = $[2];
  }
  const p = t1;
  const tone = TONE_CLASSES[p.tone];
  const label = payload.winningOutcomeLabel ?? "";
  const winnerColor = useMarketOutcomeColor(payload.marketPubkey, payload.winnerOutcomeIndex ?? 0);
  let t2;
  bb0: {
    switch (payload.variant) {
      case "graduation-generic":
        {
          let t3;
          if ($[3] !== t) {
            t3 = t("graduationGeneric.headline");
            $[3] = t;
            $[4] = t3;
          } else {
            t3 = $[4];
          }
          let t4;
          if ($[5] !== t) {
            t4 = t("graduationGeneric.body");
            $[5] = t;
            $[6] = t4;
          } else {
            t4 = $[6];
          }
          let t5;
          if ($[7] !== t3 || $[8] !== t4) {
            t5 = {
              headline: t3,
              body: t4
            };
            $[7] = t3;
            $[8] = t4;
            $[9] = t5;
          } else {
            t5 = $[9];
          }
          t2 = t5;
          break bb0;
        }
      case "graduation-creator":
        {
          let t3;
          if ($[10] !== t) {
            t3 = t("graduationCreator.headline");
            $[10] = t;
            $[11] = t3;
          } else {
            t3 = $[11];
          }
          let t4;
          if ($[12] !== t) {
            t4 = t("graduationCreator.body");
            $[12] = t;
            $[13] = t4;
          } else {
            t4 = $[13];
          }
          let t5;
          if ($[14] !== t3 || $[15] !== t4) {
            t5 = {
              headline: t3,
              body: t4
            };
            $[14] = t3;
            $[15] = t4;
            $[16] = t5;
          } else {
            t5 = $[16];
          }
          t2 = t5;
          break bb0;
        }
      case "graduation-backer":
        {
          let t3;
          if ($[17] !== t) {
            t3 = t("graduationBacker.headline");
            $[17] = t;
            $[18] = t3;
          } else {
            t3 = $[18];
          }
          let t4;
          if ($[19] !== t) {
            t4 = t("graduationBacker.body");
            $[19] = t;
            $[20] = t4;
          } else {
            t4 = $[20];
          }
          let t5;
          if ($[21] !== t3 || $[22] !== t4) {
            t5 = {
              headline: t3,
              body: t4
            };
            $[21] = t3;
            $[22] = t4;
            $[23] = t5;
          } else {
            t5 = $[23];
          }
          t2 = t5;
          break bb0;
        }
      case "resolution-won-nonbacker":
        {
          let t3;
          if ($[24] !== t) {
            t3 = t("wonNonbacker.headline");
            $[24] = t;
            $[25] = t3;
          } else {
            t3 = $[25];
          }
          let t4;
          if ($[26] !== label || $[27] !== t) {
            t4 = t("wonNonbacker.body", {
              label
            });
            $[26] = label;
            $[27] = t;
            $[28] = t4;
          } else {
            t4 = $[28];
          }
          let t5;
          if ($[29] !== t3 || $[30] !== t4) {
            t5 = {
              headline: t3,
              body: t4
            };
            $[29] = t3;
            $[30] = t4;
            $[31] = t5;
          } else {
            t5 = $[31];
          }
          t2 = t5;
          break bb0;
        }
      case "resolution-won-backer":
        {
          let t3;
          if ($[32] !== t) {
            t3 = t("wonBacker.headline");
            $[32] = t;
            $[33] = t3;
          } else {
            t3 = $[33];
          }
          let t4;
          if ($[34] !== label || $[35] !== t) {
            t4 = t("wonBacker.body", {
              label
            });
            $[34] = label;
            $[35] = t;
            $[36] = t4;
          } else {
            t4 = $[36];
          }
          let t5;
          if ($[37] !== t3 || $[38] !== t4) {
            t5 = {
              headline: t3,
              body: t4
            };
            $[37] = t3;
            $[38] = t4;
            $[39] = t5;
          } else {
            t5 = $[39];
          }
          t2 = t5;
          break bb0;
        }
      case "resolution-lost-nonbacker":
        {
          let t3;
          if ($[40] !== t) {
            t3 = t("lostNonbacker.headline");
            $[40] = t;
            $[41] = t3;
          } else {
            t3 = $[41];
          }
          let t4;
          if ($[42] !== label || $[43] !== t) {
            t4 = t("lostNonbacker.body", {
              label
            });
            $[42] = label;
            $[43] = t;
            $[44] = t4;
          } else {
            t4 = $[44];
          }
          let t5;
          if ($[45] !== t3 || $[46] !== t4) {
            t5 = {
              headline: t3,
              body: t4
            };
            $[45] = t3;
            $[46] = t4;
            $[47] = t5;
          } else {
            t5 = $[47];
          }
          t2 = t5;
          break bb0;
        }
      case "resolution-lost-backer":
        {
          let t3;
          if ($[48] !== t) {
            t3 = t("lostBacker.headline");
            $[48] = t;
            $[49] = t3;
          } else {
            t3 = $[49];
          }
          let t4;
          if ($[50] !== label || $[51] !== t) {
            t4 = t("lostBacker.body", {
              label
            });
            $[50] = label;
            $[51] = t;
            $[52] = t4;
          } else {
            t4 = $[52];
          }
          let t5;
          if ($[53] !== t3 || $[54] !== t4) {
            t5 = {
              headline: t3,
              body: t4
            };
            $[53] = t3;
            $[54] = t4;
            $[55] = t5;
          } else {
            t5 = $[55];
          }
          t2 = t5;
          break bb0;
        }
      case "resolution-not-holder":
        {
          let t3;
          if ($[56] !== t) {
            t3 = t("notHolder.headline");
            $[56] = t;
            $[57] = t3;
          } else {
            t3 = $[57];
          }
          let t4;
          if ($[58] !== label || $[59] !== t) {
            t4 = t("notHolder.body", {
              label
            });
            $[58] = label;
            $[59] = t;
            $[60] = t4;
          } else {
            t4 = $[60];
          }
          let t5;
          if ($[61] !== t3 || $[62] !== t4) {
            t5 = {
              headline: t3,
              body: t4
            };
            $[61] = t3;
            $[62] = t4;
            $[63] = t5;
          } else {
            t5 = $[63];
          }
          t2 = t5;
          break bb0;
        }
    }
    t2 = undefined;
  }
  const copy = t2;
  let t3;
  if ($[64] !== tone.iconWrap) {
    t3 = cn("flex size-16 items-center justify-center rounded-full", tone.iconWrap);
    $[64] = tone.iconWrap;
    $[65] = t3;
  } else {
    t3 = $[65];
  }
  const t4 = p.heroIcon;
  let t5;
  if ($[66] !== tone.icon) {
    t5 = cn("size-8", tone.icon);
    $[66] = tone.icon;
    $[67] = t5;
  } else {
    t5 = $[67];
  }
  let t6;
  if ($[68] !== p.heroIcon || $[69] !== t5) {
    t6 = <Icon icon={t4} className={t5} aria-hidden="true" />;
    $[68] = p.heroIcon;
    $[69] = t5;
    $[70] = t6;
  } else {
    t6 = $[70];
  }
  let t7;
  if ($[71] !== t3 || $[72] !== t6) {
    t7 = <div className={t3}>{t6}</div>;
    $[71] = t3;
    $[72] = t6;
    $[73] = t7;
  } else {
    t7 = $[73];
  }
  let t8;
  if ($[74] !== copy.headline) {
    t8 = <h2 className="text-heading font-bold text-foreground-primary">{copy.headline}</h2>;
    $[74] = copy.headline;
    $[75] = t8;
  } else {
    t8 = $[75];
  }
  const t9 = scheduledBody ?? copy.body;
  let t10;
  if ($[76] !== t9) {
    t10 = <p className="text-body text-foreground-secondary">{t9}</p>;
    $[76] = t9;
    $[77] = t10;
  } else {
    t10 = $[77];
  }
  let t11;
  if ($[78] !== t10 || $[79] !== t8) {
    t11 = <div className="flex flex-col items-center gap-2">{t8}{t10}</div>;
    $[78] = t10;
    $[79] = t8;
    $[80] = t11;
  } else {
    t11 = $[80];
  }
  let t12;
  if ($[81] !== p.showWinnerChip || $[82] !== payload.winningOutcomeLabel || $[83] !== winnerColor) {
    t12 = p.showWinnerChip && payload.winningOutcomeLabel !== null ? <span className={cn("inline-flex w-fit items-center gap-1.5 rounded-pill px-2.5 py-1", winnerColor.outline)}><Icon icon={Award01IconSolid} className={cn("size-3.5", winnerColor.text)} aria-hidden="true" /><span className={cn("text-caption font-medium", winnerColor.text)}>{payload.winningOutcomeLabel}</span></span> : null;
    $[81] = p.showWinnerChip;
    $[82] = payload.winningOutcomeLabel;
    $[83] = winnerColor;
    $[84] = t12;
  } else {
    t12 = $[84];
  }
  const t13 = scheduledBody !== null;
  let t14;
  if ($[85] !== onClose || $[86] !== p.primary || $[87] !== payload || $[88] !== t13) {
    t14 = <LifecyclePrimaryAction payload={payload} primary={p.primary} scheduled={t13} onClose={onClose} />;
    $[85] = onClose;
    $[86] = p.primary;
    $[87] = payload;
    $[88] = t13;
    $[89] = t14;
  } else {
    t14 = $[89];
  }
  let t15;
  if ($[90] !== onClose || $[91] !== p.showBackerCta || $[92] !== t) {
    t15 = p.showBackerCta ? <AnimatedButton variant="secondary" size="lg" className="w-full" onClick={onClose}>{t("cta.claimBackerRewards")}</AnimatedButton> : null;
    $[90] = onClose;
    $[91] = p.showBackerCta;
    $[92] = t;
    $[93] = t15;
  } else {
    t15 = $[93];
  }
  let t16;
  if ($[94] !== t14 || $[95] !== t15) {
    t16 = <div className="flex w-full flex-col gap-2 pt-2">{t14}{t15}</div>;
    $[94] = t14;
    $[95] = t15;
    $[96] = t16;
  } else {
    t16 = $[96];
  }
  let t17;
  if ($[97] !== t11 || $[98] !== t12 || $[99] !== t16 || $[100] !== t7) {
    t17 = <div className="relative flex flex-col items-center gap-4 text-center">{t7}{t11}{t12}{t16}</div>;
    $[97] = t11;
    $[98] = t12;
    $[99] = t16;
    $[100] = t7;
    $[101] = t17;
  } else {
    t17 = $[101];
  }
  return t17;
}
function useScheduledGraduationCopy(payload) {
  const $ = _c(4);
  const tEvent = useTranslations("liveEvents.eventTimeCard");
  const locale = useLocale();
  const nowMs = useLiveClock(1000, payload.tradingStartAt !== undefined, payload.tradingObservedAtMs ?? 0);
  if (payload.stage !== "graduation" || payload.tradingStartAt === undefined) {
    return null;
  }
  const scheduled = nowMs < Date.parse(payload.tradingStartAt);
  if (!scheduled) {
    return null;
  }
  let t0;
  if ($[0] !== locale || $[1] !== payload.tradingStartAt || $[2] !== tEvent) {
    t0 = tEvent("eventStarts", {
      date: formatLocalDateTime(new Date(payload.tradingStartAt), locale)
    });
    $[0] = locale;
    $[1] = payload.tradingStartAt;
    $[2] = tEvent;
    $[3] = t0;
  } else {
    t0 = $[3];
  }
  return t0;
}
function LifecyclePrimaryAction(t0) {
  const $ = _c(15);
  const {
    payload,
    primary,
    scheduled,
    onClose
  } = t0;
  const t = useTranslations("market.lifecycle_modal");
  const tMarket = useTranslations("market.resolvedPanel");
  if (primary === "browse") {
    let t1;
    if ($[0] !== t) {
      t1 = t("cta.browse");
      $[0] = t;
      $[1] = t1;
    } else {
      t1 = $[1];
    }
    let t2;
    if ($[2] !== onClose || $[3] !== t1) {
      t2 = <AnimatedButton asChild={true} variant="primary" size="lg" className="w-full"><Link href="/markets" onClick={onClose}>{t1}</Link></AnimatedButton>;
      $[2] = onClose;
      $[3] = t1;
      $[4] = t2;
    } else {
      t2 = $[4];
    }
    return t2;
  }
  if (primary === "trade") {
    let t1;
    if ($[5] !== scheduled || $[6] !== t || $[7] !== tMarket) {
      t1 = scheduled ? tMarket("viewMarket") : t("cta.trade");
      $[5] = scheduled;
      $[6] = t;
      $[7] = tMarket;
      $[8] = t1;
    } else {
      t1 = $[8];
    }
    let t2;
    if ($[9] !== onClose || $[10] !== t1) {
      t2 = <AnimatedButton variant="primary" size="lg" className="w-full" onClick={onClose}>{t1}</AnimatedButton>;
      $[9] = onClose;
      $[10] = t1;
      $[11] = t2;
    } else {
      t2 = $[11];
    }
    return t2;
  }
  let t1;
  if ($[12] !== onClose || $[13] !== payload) {
    t1 = <LifecycleClaimAction payload={payload} onClose={onClose} />;
    $[12] = onClose;
    $[13] = payload;
    $[14] = t1;
  } else {
    t1 = $[14];
  }
  return t1;
}
function LifecycleClaimAction(t0) {
  const $ = _c(8);
  const {
    payload,
    onClose
  } = t0;
  const t = useTranslations("market.lifecycle_modal");
  const claim = payload.claim;
  const claimClaimed = claim?.claimed === true;
  const claimReady = claim?.finalized === true && claim?.claimable === true && !claimClaimed;
  let t1;
  if ($[0] !== claimClaimed || $[1] !== claimReady || $[2] !== t) {
    t1 = claimClaimed ? t("cta.claimed") : claimReady ? t("cta.claim") : t("cta.claimPending");
    $[0] = claimClaimed;
    $[1] = claimReady;
    $[2] = t;
    $[3] = t1;
  } else {
    t1 = $[3];
  }
  const claimLabel = t1;
  const t2 = !claimReady;
  let t3;
  if ($[4] !== claimLabel || $[5] !== onClose || $[6] !== t2) {
    t3 = <AnimatedButton variant="primary" size="lg" className="w-full" onClick={onClose} disabled={t2}>{claimLabel}</AnimatedButton>;
    $[4] = claimLabel;
    $[5] = onClose;
    $[6] = t2;
    $[7] = t3;
  } else {
    t3 = $[7];
  }
  return t3;
}